import { Box, Card, VStack } from '@chakra-ui/react'
import { createFileRoute } from '@tanstack/react-router'
import mqtt from 'mqtt'
import { useEffect, useRef, useState } from 'react'
import Chat from '~/components/Chat'
import { PageHeader } from '~/components/shell'

export const Route = createFileRoute('/_app/oilandgas/bot')({ component: OilAndGasUnsBot })

const hivemqws = import.meta.env.VITE_SHARED_WS_ADDRESS

const OG_BOT_TOPIC_FILTERS = ['ecorp/upstream/#', 'ecorp/midstream/#', 'ecorp/downstream/#']

interface TopicNode {
  name: string
  attributes: {
    index: number
    value?: string
  }
  children?: TopicNode[]
}

const topicsRoot: TopicNode = {
  name: 'Root',
  attributes: {
    index: 0,
  },
  children: [],
}

const uns_options: mqtt.IClientOptions = {
  protocol: 'wss',
  port: 443,
  keepalive: 20,
  clientId: `stories_uns_oilandgas_bot_${Math.random().toString(16).substr(2, 8)}`,
  username: import.meta.env.VITE_ENERGY_WS_USERNAME,
  password: import.meta.env.VITE_ENERGY_WS_PASSWORD,
}

let uns_client: mqtt.MqttClient | null = null

function OilAndGasUnsBot() {
  const promptinit = `ROLE
You are an analytics expert for the ECorp oil & gas Unified Namespace. You analyse live MQTT telemetry across the full hydrocarbon value chain: upstream production wells, midstream pipelines, and downstream refining.

CONTEXT
Topics flow under ecorp/upstream/*, ecorp/midstream/*, and ecorp/downstream/*. The dataset is a snapshot of the live MQTT tree. Field reference:
- Upstream wells (ecorp/upstream/<basin>/<field>/<pad>/well-<N>/<equipment>/telemetry): discharge_pressure_psi, intake_pressure_psi, motor_temp_c, motor_current_a, vibration_mms, flow_rate_bpd, water_cut_pct, gas_oil_ratio, state. Tagged with basin, field, pad, equipment (typically electric submersible pump / ESP).
- Midstream pipelines (ecorp/midstream/pipeline/<name>/segment-<N>/<equipment>/telemetry): event_type, methane_ppm, pressure_drop_bar, flow_anomaly_pct, geo_lat, geo_lon, severity, recommended_action.
- Downstream refinery (ecorp/downstream/refinery/<site>/unit-<N>/telemetry): throughput_bpd, feed_temp_c, skin_temp_c, heater_duty_mw, pressure_bar, fuel_gas_flow_kgh, state.

ANSWER RULES
- Answer only from the dataset below. Never invent assets, basins, segments, or fields that are not present.
- Lead with the result: one short sentence, a small bullet list, or a compact markdown table.
- Follow with at most 2 to 4 sentences of plain-language explanation. Do not exceed that.
- Always include units (bpd, psi, bar, °C, MW, mm/s, ppm, kg/h, %).
- Round numeric values to two decimals where appropriate.
- For comparisons across wells, segments, or units, prefer a markdown table to prose.
- If the dataset does not contain enough information to answer, say so in one sentence and stop.
- Never include code, pseudocode, SQL, JSON, schemas, formulas, or step-by-step calculations. The user wants the conclusion, not the method.
- No greetings, no caveats, no mention of being an AI, no emojis.

DATASET (live MQTT topic tree, JSON):
`
  const introMessage =
    "Hi, I'm OGBot. Ask me anything about wells, pipelines, or refinery telemetry in the ECorp UNS."
  const questionList = [
    'Are any pipeline segments reporting a methane leak or pressure drop anomaly? List the segments with severity and recommended action.',
    'What is the total production rate (in barrels per day) across all wells? Group by basin if possible.',
    'Which well has the highest motor temperature on its ESP, and is it approaching a thermal limit?',
    'What is the average water cut percentage across all wells? Which well has the highest, and what does that imply?',
    'Compare the throughput (in bpd) of each refinery unit. Which is operating closest to capacity?',
    'Are there any wells in a non-running state? List the well IDs and their last reported state.',
    'Which refinery unit has the highest skin temperature, and is the heater duty proportionate?',
    'What is the highest vibration reading across all wells (mm/s), and is it associated with an alarm?',
    'List pipeline segments where flow_anomaly_pct exceeds 10%. What is the recommended action for each?',
  ]

  const [topics, setTopics] = useState<TopicNode>(topicsRoot)
  const [isConnected, setIsConnected] = useState(false)
  const [messagesPerSecond, setMessagesPerSecond] = useState(0)
  const messageCountRef = useRef(0)
  const lastCountRef = useRef(0)
  const mpsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  function connect_mqtt(broker: string) {
    uns_client?.end()
    uns_client = mqtt.connect(`wss://${broker}:443/mqtt`, uns_options)

    uns_client.on('message', (topic, message) => {
      messageCountRef.current++
      updateTreeWithNewNode(topic.toString(), message.toString())
    })

    uns_client.on('connect', () => {
      console.log(`Connected to ${broker}`)
      setIsConnected(true)
      uns_client?.subscribe(OG_BOT_TOPIC_FILTERS, { qos: 1 }, (err) => {
        if (err) {
          console.log(err)
        }
      })
    })

    uns_client.on('close', () => {
      setIsConnected(false)
    })

    uns_client.on('error', (err) => {
      console.log('Error happened')
      console.log(err)
    })
  }

  async function disconnect_mqtt(client: mqtt.MqttClient | null) {
    await client?.end()
    console.log('Disconnected')
  }

  function updateTreeWithNewNode(topic: string, message: string) {
    let parsedValue: unknown
    try {
      const parsed = JSON.parse(message)
      parsedValue = parsed.value ?? parsed
    } catch {
      parsedValue = message
    }

    const segments = topic.split('/')
    const nextData = Object.assign({}, topics)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let currentNode: any = nextData
    segments.forEach((segment: string) => {
      if (!currentNode.children) {
        currentNode.children = []
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let existingNode = currentNode.children.find((n: any) => n.name === segment)
      if (!existingNode) {
        const newNode = {
          name: segment,
          attributes: { index: 1, value: parsedValue },
        }
        currentNode.children.push(newNode)
        existingNode = newNode
      } else {
        existingNode.attributes.index = existingNode.attributes.index + 1
        existingNode.attributes.value = parsedValue
      }
      currentNode = existingNode
    })
    setTopics(nextData)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: connect/disconnect run once on mount by design, not on every render
  useEffect(() => {
    if (hivemqws) {
      connect_mqtt(hivemqws)
    }
    mpsIntervalRef.current = setInterval(() => {
      const current = messageCountRef.current
      const mps = current - lastCountRef.current
      lastCountRef.current = current
      setMessagesPerSecond(mps)
    }, 1000)
    return () => {
      disconnect_mqtt(uns_client)
      if (mpsIntervalRef.current) {
        clearInterval(mpsIntervalRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box p={6} h="calc(100vh - 120px)">
      <VStack align="stretch" gap={4} h="100%">
        <PageHeader
          title="Oil & Gas - AI Bot"
          subtitle="Ask AI to extract information from the UNS"
        />

        <Card.Root flex={1} overflow="hidden" boxShadow="2xl" display="flex" flexDirection="column">
          <Card.Body overflow="auto" flex="1" minH={0}>
            <Box h="100%">
              <Chat
                introMessage={introMessage}
                promptinit={promptinit}
                topics={topics}
                questionList={questionList}
                isConnected={isConnected}
                messagesPerSecond={messagesPerSecond}
              />
            </Box>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Box>
  )
}
